import { gql } from "@apollo/client";
import { useEffect, useState } from "react";

import { apolloClient } from "../services/backend_gateway/apollo-client";

/**
 * Account-scoped list of every pull source (data sources + mappings) with its
 * completeness counts. Shared by the Data Hub (which renders all of them) and
 * the planning-console mapping-gap alert (which looks only at the Mappings
 * group). Kept in one place so the two consumers can't drift.
 */
export const GET_MY_PULL_SOURCES = gql`
  query GetMyPullSources {
    getMyPullSources {
      name
      displayName
      description
      originalSource
      ingestionLocation
      method
      group
      lastEditedAt
      recordCount
      incompleteRowCount
    }
  }
`;

export type PullSource = {
  name: string;
  displayName: string | null;
  description: string | null;
  originalSource: string | null;
  ingestionLocation: string | null;
  method: string | null;
  group: string | null;
  lastEditedAt: string | null;
  recordCount: number | null;
  incompleteRowCount: number | null;
};

/** Pull sources whose `group` lands them in the Data Hub "Mappings" tab. */
const MAPPINGS_GROUP = "Mappings";

export type MappingGaps = {
  /** Number of Mappings-tab sources that have at least one incomplete row. */
  count: number;
  /** Tooltip lines: a summary header followed by one line per gapped source. */
  messages: string[];
};

/**
 * Surfaces, for the planning console header, how many Mappings-tab sources have
 * gaps (rows failing the source's completeness rule, as reported by the backend
 * `incompleteRowCount`). Drives the `WarningIcon` alert — same gap definition
 * the Data Hub uses for its red mapping status dots, so the two never disagree.
 *
 * Fetched once on mount (the query is account-scoped, not run-scoped). Failures
 * degrade silently to no alert rather than blocking the planning console.
 */
export const useMappingGaps = (): MappingGaps => {
  const [gaps, setGaps] = useState<MappingGaps>({ count: 0, messages: [] });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await apolloClient.query<{ getMyPullSources: PullSource[] }>({
          query: GET_MY_PULL_SOURCES,
          fetchPolicy: "cache-first",
        });
        if (cancelled) return;

        const gapped = (result.data?.getMyPullSources ?? []).filter(
          (src) => src.group === MAPPINGS_GROUP && (src.incompleteRowCount ?? 0) > 0,
        );

        if (gapped.length === 0) {
          setGaps({ count: 0, messages: [] });
          return;
        }

        const messages = [
          "Critical mappings missing — click to review:",
          ...gapped.map(
            (src) =>
              `${src.displayName ?? src.name}: ${src.incompleteRowCount?.toLocaleString()} incomplete`,
          ),
        ];
        setGaps({ count: gapped.length, messages });
      } catch (err) {
        console.warn("[useMappingGaps] fetch failed; suppressing alert", err);
        if (!cancelled) setGaps({ count: 0, messages: [] });
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  return gaps;
};
